import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_PATH } from '../../config/adminPath';
import { useTranslation } from 'react-i18next';
import { MessageSquare, X, Send, Shield, Eye, EyeOff, Lock, Database } from 'lucide-react';
import logger from '../../utils/logger';

const RGPD = () => {
  const { t } = useTranslation('public');
  const navigate = useNavigate();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAdminCodeModal, setShowAdminCodeModal] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [expectedCode, setExpectedCode] = useState('');
  const [contactForm, setContactForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    message: ''
  });

  // Récupérer le code d'accès depuis l'API
  useEffect(() => {
    const fetchAccessCode = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/system/admin-access-code`);
        if (response.ok) {
          const data = await response.json();
          setExpectedCode(data.code);
        }
      } catch (error) {
        logger.error('Erreur récupération code accès:', error);
      }
    };
    fetchAccessCode();
  }, []);

  // Détection du raccourci clavier secret Ctrl+Shift+A (3 fois)
  const [keyPressCount, setKeyPressCount] = useState(0);
  const [lastKeyTime, setLastKeyTime] = useState(0);

  const handleKeyDown = useCallback((e) => {
    // Vérifier Ctrl+Shift+A
    if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      const now = Date.now();
      
      // Reset si plus de 2 secondes depuis la dernière pression
      if (now - lastKeyTime > 2000) {
        setKeyPressCount(1);
      } else {
        setKeyPressCount(prev => prev + 1);
      }
      setLastKeyTime(now);
    }
  }, [lastKeyTime]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Afficher le modal quand 3 pressions détectées
  useEffect(() => {
    if (keyPressCount >= 3) {
      setShowAdminCodeModal(true);
      setKeyPressCount(0);
      setAdminCode('');
      setCodeError('');
    }
  }, [keyPressCount]);

  const handleAdminCodeSubmit = (e) => {
    e.preventDefault();
    if (adminCode === expectedCode) {
      setShowAdminCodeModal(false);
      navigate(`/${ADMIN_PATH}`);
    } else {
      setCodeError('Code incorrect');
      setAdminCode('');
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    logger.debug('Formulaire contact depuis RGPD:', contactForm);
    alert('Votre message a été envoyé avec succès !');
    setShowContactModal(false);
    setContactForm({ nom: '', prenom: '', email: '', telephone: '', message: '' });
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            
            {/* Bouton Contact flottant */}
            <div className="fixed bottom-6 right-6 z-40">
              <button
                onClick={() => setShowContactModal(true)}
                className="bg-brand-primary text-white p-4 rounded-full shadow-lg hover:bg-brand-primary-hover transition-all duration-300 hover:scale-110"
                title="Nous contacter"
              >
                <MessageSquare size={24} />
              </button>
            </div>

            <div className="flex items-center mb-8">
              <Shield className="text-brand-primary mr-4" size={36} />
              <h1 className="text-3xl font-bold text-brand-text">{t('legal.rgpd.title')}</h1>
            </div>
            
            <div className="space-y-8 text-gray-700 leading-relaxed">
              
              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4 flex items-center">
                  <Database className="mr-2" size={20} />
                  {t('legal.rgpd.protection')}
                </h2>
                <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-brand-primary">
                  <p className="font-medium mb-2">
                    {t('legal.rgpd.protectionIntro')}
                  </p>
                  <p>
                    {t('legal.rgpd.protectionText')}
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4 flex items-center">
                  <Eye className="mr-2" size={20} />
                  {t('legal.rgpd.collection')}
                </h2>
                <p className="mb-4">
                  {t('legal.rgpd.collectionIntro')}
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>{t('legal.rgpd.collectionItems.quotes').split(':')[0]} :</strong> {t('legal.rgpd.collectionItems.quotes').split(':')[1]}</li>
                  <li><strong>{t('legal.rgpd.collectionItems.contracts').split(':')[0]} :</strong> {t('legal.rgpd.collectionItems.contracts').split(':')[1]}</li>
                  <li><strong>{t('legal.rgpd.collectionItems.appointments').split(':')[0]} :</strong> {t('legal.rgpd.collectionItems.appointments').split(':')[1]}</li>
                  <li><strong>{t('legal.rgpd.collectionItems.tracking').split(':')[0]} :</strong> {t('legal.rgpd.collectionItems.tracking').split(':')[1]}</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.rgpd.dataTypes')}</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">{t('legal.rgpd.identificationData')}</h4>
                    <ul className="text-sm space-y-1">
                      <li>• {t('legal.rgpd.identificationItems.name')}</li>
                      <li>• {t('legal.rgpd.identificationItems.email')}</li>
                      <li>• {t('legal.rgpd.identificationItems.phone')}</li>
                      <li>• {t('legal.rgpd.identificationItems.address')}</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">{t('legal.rgpd.professionalData')}</h4>
                    <ul className="text-sm space-y-1">
                      <li>• {t('legal.rgpd.professionalItems.activity')}</li>
                      <li>• {t('legal.rgpd.professionalItems.company')}</li>
                      <li>• {t('legal.rgpd.professionalItems.income')}</li>
                      <li>• {t('legal.rgpd.professionalItems.risks')}</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.rgpd.usage')}</h2>
                <p className="mb-4">{t('legal.rgpd.usageIntro')}</p>
                <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
                  <ul className="space-y-2">
                    <li><strong>✓ {t('legal.rgpd.usageItems.quotes')}</strong></li>
                    <li><strong>✓ {t('legal.rgpd.usageItems.contracts')}</strong></li>
                    <li><strong>✓ {t('legal.rgpd.usageItems.contact')}</strong></li>
                    <li><strong>✓ {t('legal.rgpd.usageItems.legal')}</strong></li>
                    <li><strong>✓ {t('legal.rgpd.usageItems.improve')}</strong></li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.rgpd.retention')}</h2>
                <p>
                  {t('legal.rgpd.retentionText')}
                </p>
                <ul className="mt-4 space-y-2 ml-4">
                  <li>• <strong>{t('legal.rgpd.retentionItems.prospects')}</strong></li>
                  <li>• <strong>{t('legal.rgpd.retentionItems.clients')}</strong></li>
                  <li>• <strong>{t('legal.rgpd.retentionItems.accounting')}</strong></li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4 flex items-center">
                  <Lock className="mr-2" size={20} />
                  {t('legal.rgpd.rights')}
                </h2>
                <p className="mb-4">
                  {t('legal.rgpd.rightsIntro')}
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-brand-primary mb-2">{t('legal.rgpd.rightsList.access.title')}</h4>
                    <p className="text-sm">{t('legal.rgpd.rightsList.access.description')}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-brand-primary mb-2">{t('legal.rgpd.rightsList.rectification.title')}</h4>
                    <p className="text-sm">{t('legal.rgpd.rightsList.rectification.description')}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-brand-primary mb-2">{t('legal.rgpd.rightsList.erasure.title')}</h4>
                    <p className="text-sm">{t('legal.rgpd.rightsList.erasure.description')}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-brand-primary mb-2">{t('legal.rgpd.rightsList.portability.title')}</h4>
                    <p className="text-sm">{t('legal.rgpd.rightsList.portability.description')}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-brand-primary mb-2">{t('legal.rgpd.rightsList.opposition.title')}</h4>
                    <p className="text-sm">{t('legal.rgpd.rightsList.opposition.description')}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-brand-primary mb-2">{t('legal.rgpd.rightsList.limitation.title')}</h4>
                    <p className="text-sm">{t('legal.rgpd.rightsList.limitation.description')}</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.rgpd.exerciseRights')}</h2>
                <div className="bg-brand-primary bg-opacity-10 p-6 rounded-lg">
                  <p className="font-medium mb-2">
                    {t('legal.rgpd.exerciseRightsIntro')}
                  </p>
                  <p className="text-lg">
                    <strong>Email :</strong> gestion@asarcsg.com
                  </p>
                  <p className="mt-2 text-sm">
                    {t('legal.rgpd.exerciseRightsResponse')}
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.rgpd.security')}</h2>
                <p>
                  {t('legal.rgpd.securityText')}
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.rgpd.contactClaims')}</h2>
                <p>
                  {t('legal.rgpd.contactClaimsText')}
                </p>
              </section>

            </div>
          </div>
        </div>
      </div>

      {/* Modal Contact */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-brand-text">{t('legal.contactUs')}</h2>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                    <input
                      type="text"
                      required
                      value={contactForm.nom}
                      onChange={(e) => setContactForm({...contactForm, nom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                    <input
                      type="text"
                      required
                      value={contactForm.prenom}
                      onChange={(e) => setContactForm({...contactForm, prenom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                  <input
                    type="tel"
                    required
                    value={contactForm.telephone}
                    onChange={(e) => setContactForm({...contactForm, telephone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea
                    rows="4"
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
                    placeholder="Votre message..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-brand-primary text-white py-3 rounded-lg font-semibold hover:bg-brand-primary-hover transition-colors flex items-center justify-center"
                >
                  <Send className="mr-2" size={18} />
                  Envoyer le message
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Code Admin Secret */}
      {showAdminCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl max-w-sm w-full border border-slate-700 shadow-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center">
                  <Lock className="text-red-500 mr-2" size={20} />
                  <h2 className="text-lg font-bold text-white">Accès Restreint</h2>
                </div>
                <button
                  onClick={() => setShowAdminCodeModal(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleAdminCodeSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Code d'accès
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      autoFocus
                      value={adminCode}
                      onChange={(e) => {
                        setAdminCode(e.target.value);
                        setCodeError('');
                      }}
                      className="w-full px-4 py-3 pr-12 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Entrez le code"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {codeError && (
                    <p className="text-red-400 text-sm mt-2">{codeError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                >
                  Valider
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RGPD;